import React, { useEffect, useState } from "react";
import { FormControl, FormHelperText, InputLabel, OutlinedInput } from "@mui/material";
import { useFormControl } from "@mui/material/FormControl";
import { useTranslation } from "react-i18next";

export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default function FormField({ id, variant = "outlined", required, value, ...rest }) {
    return (
        <FormControl
            fullWidth
            variant={variant}
            defaultValue={value}
            required={required}
            sx={{ pb: 2 }}
        >
            <FormChildren
                id={id}
                required={required}
                variant={variant}
                value={value}
                {...rest}
            />
        </FormControl>
    );
};

const FormChildren = ({ id, variant = "outlined", validate, onValidationChange, ...rest }) => {

    const { required } = useFormControl()
    const { t } = useTranslation()

    const [touched, setTouched] = useState(false)

    const value = rest.value

    const handleBlur = (event) => {
        setTouched(true)
        rest.onBlur?.(event)
    }

    // explicit boolean coercion — `&&` returns the last-evaluated operand, so an empty-string `value`
    // would otherwise leak through (e.g. as `error=""`) and trigger MUI prop-type warnings on InputBase
    const isEmpty = !!(required && !value)
    const isInvalidEmail = !!(validate === "email" && value && !EMAIL_REGEX.test(value))
    const hasError = touched && (isEmpty || isInvalidEmail)

    let helperText = rest.helperText

    if (touched) {
        if (isEmpty) {
            helperText = t("msg.required")
        } else if (isInvalidEmail) {
            helperText = t("msg.invalid_email")
        }
    }

    useEffect(() => {
        onValidationChange?.(hasError)
    }, [hasError, onValidationChange])

    return (
        <>
            <InputLabel
                error={hasError}
                htmlFor={`input-${id}`}
                variant={variant}
                id={`input-${id}-label`}
            >
                {rest.label}
            </InputLabel>

            <OutlinedInput
                id={`input-${id}`}
                labelid={`input-${id}-label`}
                required={required}
                variant={variant}
                error={hasError}
                onBlur={handleBlur}
                {...rest}
            />

            {helperText && (
                <FormHelperText
                    sx={{ userSelect: "none" }}
                    error={hasError}
                    id={`input-${id}-helper-text`}
                >
                    {helperText}
                </FormHelperText>
            )}
        </>
    )
}